/**
 * GuestRequestsBanner — Bannière rouge clignotante affichée en haut de la
 * CaissePage côté Admin lorsqu'il y a des demandes d'accès Invité en attente.
 *
 * Pour chaque demande, l'Admin peut :
 *   - Approuver (en confirmant ou modifiant le rôle attribué)
 *   - Refuser
 *
 * Polling toutes les 4s.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ShieldCheck,
  Gamepad2,
  Check,
  X,
  UserPlus,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

export default function GuestRequestsBanner({ currentUser }) {
  const [requests, setRequests] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/guest-requests`, {
        params: { actor_role: "admin", status: "pending", limit: 20 },
        timeout: 8000,
      });
      const list = r.data?.requests || [];
      setRequests(list);
      if (list.length > 0) setDismissed(false);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (!currentUser || currentUser.role !== "admin") return undefined;
    fetchPending();
    const t = setInterval(fetchPending, 4000);
    return () => clearInterval(t);
  }, [currentUser, fetchPending]);

  if (!currentUser || currentUser.role !== "admin") return null;
  if (requests.length === 0 || dismissed) return null;

  const approve = async (req, role) => {
    setBusyId(req.id);
    try {
      await axios.post(
        `${API}/admin/guest-requests/${req.id}/approve?actor_role=admin`,
        { role },
      );
      toast.success(`Accès accordé à ${req.label || "Invité"} (${role})`);
      fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'approbation");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (req) => {
    setBusyId(req.id);
    try {
      await axios.post(`${API}/admin/guest-requests/${req.id}/reject?actor_role=admin`);
      toast.info(`Demande refusée`);
      fetchPending();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de refus");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="sticky top-0 z-[8000] w-full bg-rose-600/95 border-b-4 border-rose-300 shadow-2xl animate-pulse-strong"
      data-testid="guest-requests-banner"
    >
      <style>{`
        @keyframes guest-pulse {
          0%, 100% { background-color: rgba(220, 38, 38, 0.95); box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.7); }
          50% { background-color: rgba(190, 18, 60, 0.95); box-shadow: 0 0 24px 6px rgba(248, 113, 113, 0.4); }
        }
        .animate-pulse-strong { animation: guest-pulse 1.6s ease-in-out infinite; }
      `}</style>

      <div className="max-w-7xl mx-auto px-3 py-2">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-yellow-200 animate-bounce" />
          <p className="text-white font-bold text-sm uppercase tracking-wider">
            {requests.length} demande{requests.length > 1 ? "s" : ""} d'accès Invité en attente
          </p>
          <button
            onClick={() => setDismissed(true)}
            className="ml-auto text-rose-100 hover:text-white text-xs opacity-80 hover:opacity-100"
            title="Masquer (réapparaîtra à la prochaine demande)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-1.5">
          {requests.slice(0, 4).map((req) => {
            const ChosenIcon = req.chosen_role === "coach_jeux" ? Gamepad2 : ShieldCheck;
            return (
              <div
                key={req.id}
                className="bg-rose-900/40 border border-rose-300/30 rounded-lg px-2.5 py-1.5 flex flex-wrap items-center gap-2"
                data-testid={`guest-req-${req.id}`}
              >
                <UserPlus className="w-4 h-4 text-rose-200 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">
                    {req.label || "Invité"}{" "}
                    <span className="text-rose-200 text-xs font-normal">
                      · demandé à {formatTime(req.created_at)}
                    </span>
                  </p>
                  <p className="text-rose-100 text-[11px] flex items-center gap-1 truncate">
                    <ChosenIcon className="w-3 h-3" /> Souhait : {req.chosen_role === "coach_jeux" ? "Coach Jeux" : "Resp Op"}
                    {req.device_label ? ` · ${req.device_label.slice(0, 32)}` : ""}
                  </p>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    onClick={() => approve(req, "manager")}
                    disabled={busyId === req.id}
                    className="h-7 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold text-xs px-2"
                    data-testid={`guest-approve-manager-${req.id}`}
                  >
                    {busyId === req.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                    Resp Op
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => approve(req, "coach_jeux")}
                    disabled={busyId === req.id}
                    className="h-7 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-xs px-2"
                    data-testid={`guest-approve-coach-${req.id}`}
                  >
                    <Gamepad2 className="w-3 h-3 mr-1" />
                    Coach
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reject(req)}
                    disabled={busyId === req.id}
                    className="h-7 border-rose-200 bg-rose-900/60 hover:bg-rose-900 text-white text-xs px-2"
                    data-testid={`guest-reject-${req.id}`}
                  >
                    <X className="w-3 h-3 mr-0.5" />
                    Refus
                  </Button>
                </div>
              </div>
            );
          })}
          {requests.length > 4 && (
            <p className="text-rose-100 text-[11px] italic px-2">+ {requests.length - 4} autres demandes…</p>
          )}
        </div>
      </div>
    </div>
  );
}
